import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
    X,
    Upload,
    FileSpreadsheet,
    CheckCircle,
    AlertTriangle,
    XCircle,
    ChevronRight,
    ChevronLeft,
    Download,
    RefreshCw,
    Loader2,
    FileText,
    Table,
    ArrowRight,
    Settings,
    Info,
    Zap,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import {
    parseFile,
    autoMapHeaders,
    transformData,
    validateData,
    generateTemplate,
    getMappingConfidence,
    previewTransform,
    analyzeDataQuality,
    LOAN_FIELDS,
    COVENANT_FIELDS,
    FINANCIAL_UPDATE_FIELDS
} from '../../utils/importService';


const IMPORT_TYPES = {
    loans: {
        label: 'Loans',
        description: 'Import new loans to the portfolio',
        icon: FileText,
        fields: LOAN_FIELDS,
        color: 'blue'
    },
    covenants: {
        label: 'Covenants',
        description: 'Import covenant definitions for existing loans',
        icon: Table,
        fields: COVENANT_FIELDS,
        color: 'purple'
    },
    financial: {
        label: 'Financial Update',
        description: 'Update covenant values with new financial data',
        icon: RefreshCw,
        fields: FINANCIAL_UPDATE_FIELDS,
        color: 'emerald'
    }
};


const STEPS = ['select', 'upload', 'mapping', 'preview', 'complete'];

function ImportModal({ isOpen, onClose, onImportComplete, initialType = null }) {

    const [currentStep, setCurrentStep] = useState(initialType ? 1 : 0);
    const [importType, setImportType] = useState(initialType);
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState({ headers: [], data: [], errors: [], stats: {} });
    const [mapping, setMapping] = useState({});
    const [transformedData, setTransformedData] = useState({ valid: [], invalid: [], errors: [], warnings: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);


    const [advancedMode, setAdvancedMode] = useState(false);
    const [showDataQuality, setShowDataQuality] = useState(false);
    const [dataQuality, setDataQuality] = useState(null);
    const [mappingConfidence, setMappingConfidence] = useState({});
    const [previewRow, setPreviewRow] = useState(0);
    const [strictMode, setStrictMode] = useState(false);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

    const fileInputRef = useRef(null);


    const resetState = () => {
        setCurrentStep(0);
        setImportType(null);
        setFile(null);
        setParsedData({ headers: [], data: [], errors: [], stats: {} });
        setMapping({});
        setTransformedData({ valid: [], invalid: [], errors: [], warnings: [] });
        setIsLoading(false);
        setAdvancedMode(false);
        setShowDataQuality(false);
        setDataQuality(null);
        setMappingConfidence({});
        setPreviewRow(0);
        setStrictMode(false);
        setShowAdvancedOptions(false);
    };


    const handleClose = () => {
        resetState();
        onClose();
    };


    const handleSelectType = (type) => {
        setImportType(type);
        setCurrentStep(1);
    };


    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            processFile(droppedFile);
        }
    }, [importType]);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            processFile(selectedFile);
        }
    };

    const processFile = async (selectedFile) => {
        setFile(selectedFile);
        setIsLoading(true);

        try {
            const result = await parseFile(selectedFile);
            setParsedData(result);


            const fields = IMPORT_TYPES[importType].fields;
            const autoMapping = autoMapHeaders(result.headers, fields);
            setMapping(autoMapping);


            const confidence = getMappingConfidence(result.headers, fields, autoMapping);
            setMappingConfidence(confidence);


            if (advancedMode) {
                const quality = analyzeDataQuality(result.data, autoMapping, fields);
                setDataQuality(quality);
            }

            setCurrentStep(2);
        } catch (error) {
            setParsedData({ headers: [], data: [], errors: [error.message], stats: {} });
        } finally {
            setIsLoading(false);
        }
    };


    const handleMappingChange = (fieldKey, headerValue) => {
        const newMapping = {
            ...mapping,
            [fieldKey]: headerValue || undefined
        };
        setMapping(newMapping);


        const fields = IMPORT_TYPES[importType].fields;
        const confidence = getMappingConfidence(parsedData.headers, fields, newMapping);
        setMappingConfidence(confidence);
    };


    const livePreview = useMemo(() => {
        if (!parsedData.data.length || !importType) return null;
        const fields = IMPORT_TYPES[importType].fields;
        const row = parsedData.data[previewRow] || parsedData.data[0];
        return previewTransform(row, mapping, fields);
    }, [parsedData.data, mapping, importType, previewRow]);

    const handleProceedToPreview = () => {
        const fields = IMPORT_TYPES[importType].fields;
        const { transformedData: transformed, errors, warnings } = transformData(
            parsedData.data,
            mapping,
            fields,
            { strict: strictMode }
        );
        const validated = validateData(transformed, importType);


        validated.warnings = [...(warnings || []), ...(validated.warnings || [])];

        setTransformedData(validated);
        setCurrentStep(3);
    };


    const handleImport = () => {
        setIsLoading(true);


        setTimeout(() => {
            setIsLoading(false);
            setCurrentStep(4);

            if (onImportComplete) {
                onImportComplete({
                    type: importType,
                    data: transformedData.valid,
                    count: transformedData.valid.length
                });
            }
        }, 1500);
    };


    const canGoBack = currentStep > 0 && currentStep < 4;
    const handleBack = () => setCurrentStep(prev => Math.max(0, prev - 1));


    const mappingQuality = useMemo(() => {
        if (!importType) return 0;
        const fields = IMPORT_TYPES[importType].fields;
        const requiredFields = fields.filter(f => f.required);
        const mappedRequired = requiredFields.filter(f => mapping[f.key]).length;
        return Math.round((mappedRequired / requiredFields.length) * 100);
    }, [mapping, importType]);

    if (!isOpen) return null;

    const config = importType ? IMPORT_TYPES[importType] : null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">

                    {}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${config ? `bg-${config.color}-100` : 'bg-slate-100'}`}>
                                <Upload size={20} className={config ? `text-${config.color}-600` : 'text-slate-600'} />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-800">
                                    {config ? `Import ${config.label}` : 'Import Data'}
                                </h2>
                                <p className="text-sm text-slate-500">
                                    Step {currentStep + 1} of {STEPS.length}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {}
                            {currentStep > 0 && currentStep < 4 && (
                                <button
                                    onClick={() => setAdvancedMode(!advancedMode)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                        advancedMode
                                            ? 'bg-purple-100 text-purple-700'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    <Settings size={16} />
                                    Advanced
                                </button>
                            )}
                            <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-lg">
                                <X size={20} className="text-slate-500" />
                            </button>
                        </div>
                    </div>

                    {}
                    <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
                        <div className="flex items-center gap-2">
                            {STEPS.map((step, index) => (
                                <React.Fragment key={step}>
                                    <div className={`flex items-center gap-2 ${
                                        index <= currentStep ? 'text-blue-600' : 'text-slate-400'
                                    }`}>
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                            index < currentStep
                                                ? 'bg-blue-600 text-white'
                                                : index === currentStep
                                                    ? 'bg-blue-100 text-blue-600 border-2 border-blue-600'
                                                    : 'bg-slate-200 text-slate-500'
                                        }`}>
                                            {index < currentStep ? <CheckCircle size={14} /> : index + 1}
                                        </div>
                                        <span className="text-xs font-medium capitalize hidden sm:inline">{step}</span>
                                    </div>
                                    {index < STEPS.length - 1 && (
                                        <div className={`flex-1 h-0.5 ${index < currentStep ? 'bg-blue-600' : 'bg-slate-200'}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {}
                    <div className="p-6 min-h-[450px] max-h-[70vh] overflow-auto">

                        {}
                        {currentStep === 0 && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-800">What would you like to import?</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    {Object.entries(IMPORT_TYPES).map(([key, typeConfig]) => (
                                        <button
                                            key={key}
                                            onClick={() => handleSelectType(key)}
                                            className="flex flex-col items-center p-6 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                                        >
                                            <div className={`p-3 rounded-xl bg-${typeConfig.color}-100 mb-3 group-hover:scale-110 transition-transform`}>
                                                <typeConfig.icon size={24} className={`text-${typeConfig.color}-600`} />
                                            </div>
                                            <h4 className="font-semibold text-slate-800">{typeConfig.label}</h4>
                                            <p className="text-xs text-slate-500 text-center mt-1">{typeConfig.description}</p>
                                        </button>
                                    ))}
                                </div>

                                {}
                                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                                    <div className="flex items-start gap-3">
                                        <Info size={20} className="text-blue-600 mt-0.5" />
                                        <div>
                                            <h4 className="font-medium text-blue-800">Supported formats</h4>
                                            <p className="text-sm text-blue-700 mt-1">
                                                CSV, XLSX, XLS files. Numbers can be formatted as: €1.500.000, 75M, 1.2B.
                                                Dates as: DD/MM/YYYY, YYYY-MM-DD, "15 June 2025"
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {}
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-semibold text-slate-800">Upload your file</h3>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => generateTemplate(importType, { format: 'csv' })}
                                            className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                        >
                                            <Download size={16} />
                                            CSV Template
                                        </button>
                                        <button
                                            onClick={() => generateTemplate(importType, { format: 'xlsx' })}
                                            className="flex items-center gap-2 text-sm text-emerald-600 hover:underline"
                                        >
                                            <Download size={16} />
                                            Excel Template
                                        </button>
                                    </div>
                                </div>

                                {}
                                <div
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                                        isDragging
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                                    }`}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv,.xlsx,.xls,.txt"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />

                                    {isLoading ? (
                                        <div className="flex flex-col items-center">
                                            <Loader2 size={40} className="text-blue-600 animate-spin mb-3" />
                                            <p className="text-slate-600">Processing file...</p>
                                        </div>
                                    ) : (
                                        <>
                                            <FileSpreadsheet size={48} className={`mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
                                            <p className="text-lg font-medium text-slate-700">
                                                {isDragging ? 'Drop your file here' : 'Drag & drop your file here'}
                                            </p>
                                            <p className="text-sm text-slate-500 mt-1">or click to browse</p>
                                            <p className="text-xs text-slate-400 mt-4">Supported: CSV, XLSX, XLS (max 10MB)</p>
                                        </>
                                    )}
                                </div>

                                {parsedData.errors.length > 0 && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <div className="flex items-center gap-2 text-red-700 mb-2">
                                            <XCircle size={18} />
                                            <span className="font-medium">Parsing errors</span>
                                        </div>
                                        <ul className="text-sm text-red-600 space-y-1">
                                            {parsedData.errors.map((error, i) => (
                                                <li key={i}>• {error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {}
                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-lg font-semibold text-slate-800">Map columns</h3>
                                        <p className="text-sm text-slate-500">Match your file columns to the expected fields</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {}
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${
                                                mappingQuality === 100 ? 'bg-emerald-500' :
                                                    mappingQuality >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                            }`} />
                                            <span className="text-sm text-slate-600">{mappingQuality}% mapped</span>
                                        </div>
                                        <div className="text-sm text-slate-500">
                                            {parsedData.data.length} rows • {parsedData.headers.length} columns
                                        </div>
                                    </div>
                                </div>

                                {}
                                {advancedMode && parsedData.stats && (
                                    <div className="p-3 bg-slate-50 rounded-lg flex gap-6 text-sm">
                                        <div><span className="text-slate-500">Delimiter:</span> <span className="font-mono">{parsedData.stats.delimiter || 'auto'}</span></div>
                                        <div><span className="text-slate-500">Empty rows skipped:</span> {parsedData.stats.emptyRows || 0}</div>
                                        {parsedData.stats.activeSheet && (
                                            <div><span className="text-slate-500">Sheet:</span> {parsedData.stats.activeSheet}</div>
                                        )}
                                    </div>
                                )}

                                {}
                                {advancedMode && (
                                    <div className="border border-slate-200 rounded-lg">
                                        <button
                                            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Settings size={16} className="text-slate-500" />
                                                <span className="font-medium text-slate-700">Advanced Options</span>
                                            </div>
                                            {showAdvancedOptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </button>
                                        {showAdvancedOptions && (
                                            <div className="px-4 py-3 border-t border-slate-200 space-y-3">
                                                <label className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={strictMode}
                                                        onChange={(e) => setStrictMode(e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 rounded"
                                                    />
                                                    <div>
                                                        <span className="font-medium text-slate-700">Strict Mode</span>
                                                        <p className="text-xs text-slate-500">Reject rows with any parsing errors</p>
                                                    </div>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {}
                                <div className="border border-slate-200 rounded-xl overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Required Field</th>
                                            <th className="text-center px-4 py-3 w-12"><ArrowRight size={16} className="mx-auto text-slate-400" /></th>
                                            <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Your Column</th>
                                            {advancedMode && (
                                                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600 w-24">Confidence</th>
                                            )}
                                            <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Preview</th>
                                        </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                        {config.fields.map((field) => {
                                            const selectedHeader = mapping[field.key];
                                            const preview = livePreview?.[field.key];
                                            const confidence = mappingConfidence[field.key];

                                            return (
                                                <tr key={field.key} className="hover:bg-slate-50">
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-slate-800">{field.label}</span>
                                                            {field.required && (
                                                                <span className="text-xs text-red-500">*</span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-slate-400">{field.type}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <ArrowRight size={16} className="mx-auto text-slate-300" />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            value={selectedHeader || ''}
                                                            onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                                                field.required && !selectedHeader
                                                                    ? 'border-red-300 bg-red-50'
                                                                    : 'border-slate-300'
                                                            }`}
                                                        >
                                                            <option value="">-- Select column --</option>
                                                            {parsedData.headers.map((header) => (
                                                                <option key={header} value={header}>{header}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    {advancedMode && (
                                                        <td className="px-4 py-3">
                                                            {confidence && confidence.score > 0 && (
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`w-8 h-1.5 rounded-full ${
                                                                        confidence.score >= 90 ? 'bg-emerald-500' :
                                                                            confidence.score >= 70 ? 'bg-amber-500' : 'bg-red-400'
                                                                    }`} style={{ width: `${confidence.score * 0.8}px` }} />
                                                                    <span className="text-xs text-slate-500">{confidence.score}%</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td className="px-4 py-3">
                                                        {preview && (
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-sm font-mono truncate max-w-[120px] ${
                                                                    preview.status === 'error' ? 'text-red-600' :
                                                                        preview.status === 'missing' ? 'text-amber-600' : 'text-slate-600'
                                                                }`}>
                                                                    {String(preview.transformed).substring(0, 20)}
                                                                </span>
                                                                {preview.status === 'error' && (
                                                                    <XCircle size={14} className="text-red-500" />
                                                                )}
                                                                {preview.status === 'ok' && preview.raw !== preview.transformed && (
                                                                    <Zap size={14} className="text-blue-500" title="Auto-converted" />
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>

                                {}
                                {advancedMode && parsedData.data.length > 1 && (
                                    <div className="flex items-center justify-center gap-4">
                                        <button
                                            onClick={() => setPreviewRow(Math.max(0, previewRow - 1))}
                                            disabled={previewRow === 0}
                                            className="p-1 hover:bg-slate-100 rounded disabled:opacity-50"
                                        >
                                            <ChevronLeft size={20} />
                                        </button>
                                        <span className="text-sm text-slate-600">
                                            Preview row {previewRow + 1} of {parsedData.data.length}
                                        </span>
                                        <button
                                            onClick={() => setPreviewRow(Math.min(parsedData.data.length - 1, previewRow + 1))}
                                            disabled={previewRow === parsedData.data.length - 1}
                                            className="p-1 hover:bg-slate-100 rounded disabled:opacity-50"
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {}
                        {currentStep === 3 && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-semibold text-slate-800">Preview & Confirm</h3>
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2 text-emerald-600">
                                            <CheckCircle size={18} />
                                            <span className="text-sm font-medium">{transformedData.valid.length} valid</span>
                                        </div>
                                        {transformedData.invalid.length > 0 && (
                                            <div className="flex items-center gap-2 text-red-600">
                                                <XCircle size={18} />
                                                <span className="text-sm font-medium">{transformedData.invalid.length} errors</span>
                                            </div>
                                        )}
                                        {transformedData.warnings?.length > 0 && (
                                            <div className="flex items-center gap-2 text-amber-600">
                                                <AlertTriangle size={18} />
                                                <span className="text-sm font-medium">{transformedData.warnings.length} warnings</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {}
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                                        <p className="text-2xl font-bold text-slate-800">{parsedData.data.length}</p>
                                        <p className="text-xs text-slate-500">Total Rows</p>
                                    </div>
                                    <div className="p-3 bg-emerald-50 rounded-lg text-center">
                                        <p className="text-2xl font-bold text-emerald-600">{transformedData.valid.length}</p>
                                        <p className="text-xs text-emerald-700">Ready to Import</p>
                                    </div>
                                    <div className="p-3 bg-red-50 rounded-lg text-center">
                                        <p className="text-2xl font-bold text-red-600">{transformedData.invalid.length}</p>
                                        <p className="text-xs text-red-700">Errors</p>
                                    </div>
                                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                                        <p className="text-2xl font-bold text-amber-600">{transformedData.warnings?.length || 0}</p>
                                        <p className="text-xs text-amber-700">Warnings</p>
                                    </div>
                                </div>

                                {}
                                {transformedData.errors.length > 0 && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg max-h-32 overflow-auto">
                                        <div className="flex items-center gap-2 text-red-700 mb-2">
                                            <XCircle size={18} />
                                            <span className="font-medium">Validation errors (rows will be skipped)</span>
                                        </div>
                                        <ul className="text-sm text-red-600 space-y-1">
                                            {transformedData.errors.slice(0, 5).map((error, i) => (
                                                <li key={i}>• {error}</li>
                                            ))}
                                            {transformedData.errors.length > 5 && (
                                                <li className="text-red-500">... and {transformedData.errors.length - 5} more errors</li>
                                            )}
                                        </ul>
                                    </div>
                                )}

                                {}
                                {transformedData.warnings?.length > 0 && (
                                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg max-h-24 overflow-auto">
                                        <div className="flex items-center gap-2 text-amber-700 mb-2">
                                            <AlertTriangle size={18} />
                                            <span className="font-medium">Warnings (rows will still be imported)</span>
                                        </div>
                                        <ul className="text-sm text-amber-600 space-y-1">
                                            {transformedData.warnings.slice(0, 3).map((warning, i) => (
                                                <li key={i}>• {warning}</li>
                                            ))}
                                            {transformedData.warnings.length > 3 && (
                                                <li>... and {transformedData.warnings.length - 3} more</li>
                                            )}
                                        </ul>
                                    </div>
                                )}

                                {}
                                <div className="border border-slate-200 rounded-xl overflow-hidden">
                                    <div className="max-h-64 overflow-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2 font-semibold text-slate-600">#</th>
                                                {config.fields.slice(0, 6).map((field) => (
                                                    <th key={field.key} className="text-left px-3 py-2 font-semibold text-slate-600">
                                                        {field.label}
                                                    </th>
                                                ))}
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                            {transformedData.valid.slice(0, 10).map((row, index) => (
                                                <tr key={index} className={`hover:bg-slate-50 ${row._warnings?.length ? 'bg-amber-50/50' : ''}`}>
                                                    <td className="px-3 py-2 text-slate-400">{index + 1}</td>
                                                    {config.fields.slice(0, 6).map((field) => (
                                                        <td key={field.key} className="px-3 py-2 text-slate-700 truncate max-w-[150px]">
                                                            {row[field.key] !== null && row[field.key] !== undefined
                                                                ? field.type === 'number'
                                                                    ? Number(row[field.key]).toLocaleString()
                                                                    : String(row[field.key])
                                                                : <span className="text-slate-400">-</span>
                                                            }
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {transformedData.valid.length > 10 && (
                                        <div className="px-4 py-2 bg-slate-50 text-sm text-slate-500 text-center border-t">
                                            Showing 10 of {transformedData.valid.length} valid rows
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {}
                        {currentStep === 4 && (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                                    <CheckCircle size={32} className="text-emerald-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">Import Complete!</h3>
                                <p className="text-slate-500 mb-6">
                                    Successfully imported {transformedData.valid.length} {config.label.toLowerCase()}
                                </p>

                                {}
                                <div className="w-full max-w-md bg-slate-50 rounded-xl p-4 mb-6">
                                    <h4 className="font-medium text-slate-700 mb-3">Import Summary</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">File processed</span>
                                            <span className="font-medium text-slate-700">{file?.name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">Records imported</span>
                                            <span className="font-medium text-emerald-600">{transformedData.valid.length}</span>
                                        </div>
                                        {transformedData.invalid.length > 0 && (
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Records skipped</span>
                                                <span className="font-medium text-red-600">{transformedData.invalid.length}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    onClick={handleClose}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Done
                                </button>
                            </div>
                        )}
                    </div>

                    {}
                    {currentStep > 0 && currentStep < 4 && (
                        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
                            <button
                                onClick={handleBack}
                                disabled={!canGoBack}
                                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                            >
                                <ChevronLeft size={18} />
                                Back
                            </button>

                            {currentStep === 2 && (
                                <button
                                    onClick={handleProceedToPreview}
                                    disabled={config.fields.some(f => f.required && !mapping[f.key])}
                                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Preview
                                    <ChevronRight size={18} />
                                </button>
                            )}

                            {currentStep === 3 && (
                                <button
                                    onClick={handleImport}
                                    disabled={transformedData.valid.length === 0 || isLoading}
                                    className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            Import {transformedData.valid.length} rows
                                            <CheckCircle size={18} />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ImportModal;
